import { Box, Grid, GridItem, Collapse, IconButton, useDisclosure, Tooltip } from '@chakra-ui/react';
import React, { useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CMXIcon } from '@codametrix/ui-components';
import classnames from 'classnames';
import { commonEnums, gradeBenchPanelActions } from '@codametrix/ui-common';
const { FieldType, CodeAssessmentRiskCategory } = commonEnums;
import { workBench } from '@codametrix/ui-common';
import { MedicalCodeChip } from '@codametrix/ui-components';
import CodeFieldComponent from './field-components/code-field-component';
import TextInputComponent from './field-components/text-input-field-component';
import { getWorkflowForCodeType } from '../../highlight-drawer/rubric-utils';
import CodeQuestionsSection from '../code-questions-section';
import { AppliedClassNames } from './applied-class-names';
import '../styles/index.scss';
const { getValueAtPath, getOptionsFromTree } = workBench.pathUtils;

const IconComponent: React.FC<{ field: CMxCommonApp.IconField }> = ({
  field
}) => {
  const IconComponent = field.svgIconName ? (
    <CMXIcon
      svgIconName={field.svgIconName}
      svgIconClass={field.svgIconClass}
      svgIconSize={field.svgIconSize}
      svgViewBox={field.svgIconSize}
      svgTitleText={field.svgIconName}
    />
  ) : (
    <></>
  );

  let classnames = field.classNames;

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (field.onSelect) {
      field.onSelect(field.key);
    }
  };

  return (
    <Box onClick={handleClick} cursor={field.onSelect ? 'pointer' : 'default'}>
      <AppliedClassNames classNames={field.classNames} value={IconComponent} />
    </Box>
  );
};

const SelectListFieldComponent: React.FC<{
  field: CMxCommonApp.SelectListField;
  value: object;
}> = ({ field, value }) => {
  const handleSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    field.onSelect(field.path, e.target.value, value);
  };

  let options = field['options'] ? field['options'] : field['defaultOptions'];

  if (field.optionsTree) {
    let optionsAtTree = getOptionsFromTree(
      value,
      field.optionsTree,
      field.optionsTreePath
    );

    if (optionsAtTree) {
      options = optionsAtTree;
    }
  }

  const key = field.rerenderFields ? field.rerenderFields.map((field) => value[field]).join('-') : '';

  const valueAtPath = getValueAtPath(value, field.path);
  const optionsIncludeValue = options.includes(valueAtPath);

  return (
    <select
      className="cmx-form-select"
      key={key}
      disabled={field.readonly}
      value={valueAtPath}
      onChange={handleSelect}>
      <option value="" disabled selected>
        {field.placeholder}
      </option>
      {!optionsIncludeValue && valueAtPath && (
        <option value={valueAtPath} disabled>
          {valueAtPath} (no longer available)
        </option>
      )}
      {options.map((option) => (
        <option key={option} value={option}>
          {option}
        </option>
      ))}
    </select>
  );
};

// Placeholder component for ButtonField
const ButtonFieldComponent: React.FC<{ field: CMxCommonApp.ButtonField }> = ({
  field
}) => (
  <button onClick={() => console.log(field.action)}>
    {field.icon && <span>{field.icon}</span>}
  </button>
);

type ExpandableOptions = {
  //Use css to toggle display of component
  //Should display will recieve the rows on state and render off a value in that
  shouldDisplay: (field: CMxCommonApp.CodeLayerField[]) => boolean;
  component: React.FC<any>;
};

export type GenericCodeLayerProps = {
  // Fields are similar to form fields
  fields: CMxCommonApp.CodeLayerField[];

  /*
  An item can render an additional component below it.
  This is a property of a code layer.
  Examples:
  1. An individual code can have a code search directly below it.
  2. An artifact can have evidence directly below it.
  If we add many foldouts and expansions than it could be its own layer
  */
  expandableOptions?: ExpandableOptions;
  key: string;
  // fields get their values from the values object
  values: object;
  classNames?: string[];
  cqaCodeAssessmentRiskDictionaries: Accelerate.CQACodeAssessmentRiskDictionaries;
  /** Work item ID (t_work_item.id) for system question expression evaluation */
  workItemId?: number;
};

//Sets widths of fields using chakra grid
//React to screen size?
//Recommend 4 or less fields
//Todo : Padding fr if fr total < 10 or does muy grid automatically align center
function generateTemplateColumns(
  draggable: boolean,
  expandable: boolean,
  fieldFractions: string[]
): string {
  const calculateFractions = fieldFractions.reduce((acc, fraction) => {
    return acc + `${fraction} `;
  }, '');

  return calculateFractions;
}

function mapCategorytoSvgName(category: string) {
  if (!category) {
    return '';
  }

  switch (category) {
    case CodeAssessmentRiskCategory.CORRECT:
      return 'CheckCircle1';
    case CodeAssessmentRiskCategory.INCORRECT:
      return 'WrongCode1';
    case CodeAssessmentRiskCategory.MISSED:
      return 'PlusCircle1';
    default:
      return '';
  }
}

export function findCodeAssessmentRiskEntry(
  code: Accelerate.CQACode,
  cqaCodeAssessmentRiskDictionaries: Accelerate.CQACodeAssessmentRiskDictionaries
) {
  if (
    !code ||
    !code.codeAssessment ||
    !code.codeType ||
    !cqaCodeAssessmentRiskDictionaries
  ) {
    return null;
  }

  const assessment = code.codeAssessment.assessment;
  const role = code.codeAssessment.role;
  const codeType = code.codeType;

  return cqaCodeAssessmentRiskDictionaries[codeType].rows.find(
    (row) => row.assessment == assessment && row.role == role
  );
}

export function updateCodeAssessmentIcon(
  field: CMxCommonApp.CodeLayerField,
  code: Accelerate.CQACode,
  cqaCodeAssessmentRiskDictionaries: Accelerate.CQACodeAssessmentRiskDictionaries
) {
  if (field.key !== 'baseCQACodeAssessmentIcon') {
    return field;
  }

  const row = findCodeAssessmentRiskEntry(
    code,
    cqaCodeAssessmentRiskDictionaries
  );

  if (!row) {
    return field;
  }

  return {
    ...field,
    svgIconName: mapCategorytoSvgName(row.category)
  };
}

function componentForField(
  field: CMxCommonApp.CodeLayerField,
  code: object,
  cqaCodeAssessmentRiskDictionaries: Accelerate.CQACodeAssessmentRiskDictionaries
) {
  if (field.hidden) {
    return null;
  }
  switch (field.fieldType) {
    case FieldType.SelectList:
      return <SelectListFieldComponent field={field} value={code} />;
    case FieldType.Button:
      return <ButtonFieldComponent field={field} />;
    case FieldType.Text:
      return (
        <AppliedClassNames
          classNames={field.classNames}
          value={getValueAtPath(code, field.key)}
        />
      );
    case FieldType.Icon:
      if (field.key === 'baseCQACodeAssessmentIcon') {
        const updatedField = updateCodeAssessmentIcon(
          field,
          code as Accelerate.CQACode,
          cqaCodeAssessmentRiskDictionaries
        );
        return <IconComponent field={updatedField} />;
      }
      return <IconComponent field={field} />;
    case FieldType.Code:
      const classNamesMedicalCodeChip = [];
      const row = findCodeAssessmentRiskEntry(
        code as Accelerate.CQACode,
        cqaCodeAssessmentRiskDictionaries
      );
      if (row && row.category == CodeAssessmentRiskCategory.INCORRECT) {
        classNamesMedicalCodeChip.push('incorrect-medical-code');
      }
      return (
        <CodeFieldComponent
          field={field}
          value={code}
          classNamesMedicalCodeChip={classNamesMedicalCodeChip}
        />
      );
    case FieldType.TextInput:
      return <TextInputComponent field={field} value={code} />;
    default:
      return null; // Handle unknown field types gracefully
  }
}

function createGridItems(
  expandable: boolean,
  fieldsComponents: JSX.Element[]
): JSX.Element[] {
  let components = [];

  const fieldGridItems = fieldsComponents.map((component) => (
    // @ts-ignore - TypeScript cannot handle complex Chakra UI GridItem union types
    <GridItem w="100%">{component}</GridItem>
  ));
  components = [...components, ...fieldGridItems];

  if (expandable) {
    components.push(<GridItem w="100%"> \/</GridItem>);
  }

  return components;
}

// Could also be called "Row", it is based off of fields, nothing code specific
const GenericCodeLayer: React.FC<GenericCodeLayerProps> = (
  props: GenericCodeLayerProps
) => {
  const {
    fields,
    expandableOptions,
    values,
    classNames,
    cqaCodeAssessmentRiskDictionaries,
    workItemId
  } = props;
  const _itemRef = React.useRef<HTMLDivElement>(null);

  const dispatch = useDispatch();

  // Gate code questions behind case-level completion
  const caseLevelComplete = useSelector(
    (state: any) => state.gradeBenchPanel?.caseLevelComplete
  );
  const codeQuestionsDisabled = caseLevelComplete === false;

  // Get rubricVersion from Redux (same pattern as CaseQuestionsForm)
  const rubricVersion = useSelector(
    (state: any) => state.gradeBenchPanel?.rubricVersion
  ) as CQA.FullRubricVersion | null | undefined;
  const rubricVersionId = useSelector(
    (state: any) =>
      state.gradeBenchPanel?.qaRule?.data?.project_configuration?.rubricVersionId
  ) as number | null | undefined;

  // Expand/collapse state for questions section (expanded by default)
  const { isOpen, onToggle } = useDisclosure({ defaultIsOpen: true });

  // Get the code object from values (it includes id, codeType, attributes)
  const code = values as Accelerate.CQACode;

  const rubricWorkflow = useMemo(() => {
    if (!rubricVersion || !code.codeType) return undefined;
    return getWorkflowForCodeType(rubricVersion, code.codeType);
  }, [rubricVersion, code.codeType]);

  // Check if rubric workflow has questions
  const hasRubricWorkflow = rubricWorkflow?.questions?.length > 0;
  const hasRubricVersion = Boolean(rubricVersion || rubricVersionId);

  // Create handlers for attribute changes (same pattern as CaseQuestionsForm)
  const handleAnswerChange = useCallback(
    (params: CQA.UpdateAttributeParams) => {
      dispatch(gradeBenchPanelActions.updateAttribute(params) as any);
    },
    [dispatch]
  );

  const handleDeleteAnswer = useCallback(
    async (params: CQA.DeleteAttributeParams) => {
      await dispatch(gradeBenchPanelActions.deleteAttribute(params) as any);
    },
    [dispatch]
  );

  const handleClearDownstream = useCallback(
    (params: CQA.ClearDownstreamAttributesParams) => {
      dispatch(gradeBenchPanelActions.clearDownstreamAttributes(params));
    },
    [dispatch]
  );

  // Fields to hide when rubric workflow exists (Assessment/Role dropdowns and icon)
  const ASSESSMENT_ROLE_FIELD_KEYS = ['assessment', 'role', 'baseCQACodeAssessmentIcon'];

  const visibleFields = useMemo(() => {
    if (hasRubricVersion) {
      return fields.filter(field => !ASSESSMENT_ROLE_FIELD_KEYS.includes(field.key));
    }
    return fields;
  }, [fields, hasRubricVersion]);

  const columns = visibleFields.reduce((acc, field) => {
    let temp = acc;
    if (!field!.hidden) {
      temp.push(field.width);
    }
    return temp;
  }, []);

  const columnsWithButton = hasRubricWorkflow ? [...columns, '1fr'] : columns;
  const templateColumns = generateTemplateColumns(true, false, columnsWithButton);

  //Get the components for each field
  const mapped = visibleFields
    .map((field) => {
      return componentForField(
        field,
        values,
        cqaCodeAssessmentRiskDictionaries
      );
    })
    .filter((item) => item !== null);

  //Wrap component for each field
  const gridItems = createGridItems(false, mapped);

  if (hasRubricWorkflow) {
    gridItems.push(
      // @ts-ignore - TypeScript cannot handle complex Chakra UI GridItem union types
      <GridItem w="100%" display="flex" justifyContent="flex-end" key="collapse-btn">
        <Tooltip
          label="Complete case-level questions first"
          isDisabled={!codeQuestionsDisabled}
        >
          <IconButton
            aria-label={isOpen ? 'Collapse questions' : 'Expand questions'}
            icon={
              <CMXIcon
                svgIconName={isOpen ? 'BiomeChevronDownSVG20' : 'BiomeChevronRight'}
                svgIconSize="16"
              />
            }
            onClick={onToggle}
            variant="ghost"
            size="sm"
            opacity={codeQuestionsDisabled ? 0.5 : 1}
          />
        </Tooltip>
      </GridItem>
    );
  }

  //If expandable, add expandable component
  const displayExpandable = expandableOptions
    ? expandableOptions.shouldDisplay(fields)
    : false;
  const Component = expandableOptions?.component;
  const ExpandComponent = Component ? (
    <Component />
  ) : (
    <div>No expandable Options</div>
  );

  const classes = classnames(classNames);

  return (
    <Box ref={_itemRef}>
      {/* @ts-ignore - TypeScript cannot handle complex Chakra UI Grid union types */}
      <Grid
        templateColumns={templateColumns}
        gap={0}
        alignItems={'center'}
        className={classes}>
        {gridItems}
      </Grid>
      <Box display={displayExpandable ? 'block' : 'none'} className={classes}>
        {ExpandComponent}
      </Box>
      {/* Questions section BELOW the code row (collapsible) */}
      {hasRubricWorkflow && (
        <Collapse in={isOpen} animateOpacity>
          <CodeQuestionsSection
            code={code}
            rubricWorkflow={rubricWorkflow}
            onAnswerChange={handleAnswerChange}
            onDeleteAnswer={handleDeleteAnswer}
            onClearDownstream={handleClearDownstream}
            evaluateFn={gradeBenchPanelActions.evaluateWorkflow}
            workItemId={workItemId}
            isDisabled={codeQuestionsDisabled}
            isCaseIncomplete={codeQuestionsDisabled}
          />
        </Collapse>
      )}
    </Box>
  );
};

//Public Component
export default GenericCodeLayer;

//"Private" exports for unit testing
export const CodeLayerFieldComponents = {
  SelectListFieldComponent
  //Add more components as needed
};
